import debounce from "lodash/debounce";
// eslint-disable-next-line no-restricted-imports
import { useRouter } from "next/router";
import {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { useSelector } from "react-redux";
import BillingInformationStep from "rivals/components/pages/Registration/Steps/BillingInformation";
import CompletePurchaseStep from "rivals/components/pages/Registration/Steps/CompletePurchase";
import ConfirmSubscriptionStep from "rivals/components/pages/Registration/Steps/ConfirmSubscription";
import CreateAccountStep from "rivals/components/pages/Registration/Steps/CreateAccount";
import strings from "rivals/components/pages/Registration/Steps/CreateAccount/strings";
import SiteSelectionStep from "rivals/components/pages/Registration/Steps/SiteSelection";
import useError from "rivals/components/pages/Registration/useError";
import useRedirect from "rivals/components/pages/Registration/useRedirect";
import AddressVerificationModal from "rivals/components/shared/Form/BillingInformation/AddressVerificationModal";
import { INVALID_ADDRESS } from "rivals/redux/rails/constants";
import { State as AppState } from "rivals/redux/reducer";
import { selectSelectedAddOn } from "rivals/redux/registration/selectors";
import { selectSubscribableAddOns } from "rivals/redux/subscriptionAddons/selectors";
import { initialState } from "rivals/services/billing/types";
import useObick from "rivals/services/billing/useObick";
import { RegistrationParams } from "rivals/services/registration/types";
import { CUSTOM_EVENTS } from "rivals/shared/analytics/customEvents";
import {
  EMAIL_USERNAME_CHANGE_SLEEPTIME,
  PLANS,
  PurchasablePlan,
  ROOT_SITE_IDENTIFIER,
  ROOT_SITE_NAME
} from "rivals/shared/constants";
import { AddOn } from "rivals/shared/interfaces/AddOn";
import { Site } from "rivals/shared/interfaces/Site";
import { TaxAddress } from "rivals/shared/interfaces/User";
import commonStrings from "rivals/shared/strings/common";
import {
  invalidUserInput,
  isValidEmail,
  isValidPassword
} from "rivals/shared/validations";
import type { Props } from "./types";

const CreateAccount: FunctionComponent<Props> = ({
  billingConstants,
  clearAlerts,
  createAccount,
  currentSite,
  isAuthenticated,
  isRedirecting,
  isSubscribed,
  isSiteSubscribable,
  payPalEmail,
  payPalError,
  payPalAddress,
  payPalToken,
  pricingConstants,
  registrationError,
  registrationParams,
  resetPayPal,
  sendGAEvent,
  setIsRedirecting,
  submitting,
  updatePayPalError,
  user,
  validateEmailOrUsername,
  validationError,
  taxEstimationError
}) => {
  const STEP_NUMBERS = {
    FIVE: 5,
    FOUR: 4,
    ONE: 1,
    THREE: 3,
    TWO: 2
  };

  const { emailError, passwordError } = strings;
  const { payPalAccountError, payPalEmailError } = commonStrings;

  const autoRenewalOptInStates = billingConstants?.autoRenewalOptInStates || [];
  const debitTerms = billingConstants?.debitTerms || "";
  const obickSourceUrl = billingConstants?.obickSourceUrl || "";
  const states = billingConstants?.states;
  const billingPropsPresent =
    autoRenewalOptInStates.length > 0 &&
    !!debitTerms &&
    !!obickSourceUrl &&
    !!billingConstants?.smartyStreetsWebToken;

  const annualCostDollars = pricingConstants?.annualCostDollars;
  const monthlyCostDollars = pricingConstants?.monthlyCostDollars;
  const monthlySavings = pricingConstants?.monthlySavings;
  const tax = pricingConstants?.tax;
  const pricingPropsPresent =
    !!annualCostDollars && !!monthlyCostDollars && !!monthlySavings;

  const [selectedSite, setSelectedSite] = useState<Site | undefined>(
    currentSite
  );

  const [submitClicked, setSubmitClicked] = useState(false);
  const [payPalEnabledEmail, setPayPalEnabledEmail] = useState("");

  const router = useRouter();

  const {
    pathname,
    query: {
      plan,
      promo_code: promotionCode,
      ref: referAFriendCode,
      ncid: ncid
    }
  } = router;

  const referralUrlParams = {
    promoCode: promotionCode as string,
    ref: referAFriendCode as string
  };

  useRedirect({
    currentSite,
    isAuthenticated,
    isSubscribed,
    referralUrlParams,
    registrationError,
    selectedSite,
    setIsRedirecting,
    submitClicked,
    user
  });

  const {
    billingFormProps,
    submit,
    isCheckingOut,
    debitAgreedState,
    valid,
    isDebitAgreed
  } = useObick({
    autoRenewalOptInStates,
    createAccountAction: createAccount,
    formReady: !payPalToken,
    initialState,
    obickSourceUrl,
    payPalAddress,
    payPalToken
  });

  const {
    billingFormState: { city, state, street1, street2, zip },
    setIsAutoRenewAgreed,
    isAutoRenewAgreed,
    requiresAutoRenewalOptIn
  } = billingFormProps;

  const payPalLocation = {
    city: payPalAddress?.city || "",
    state: payPalAddress?.state || "",
    street1: payPalAddress?.street1 || "",
    street2: payPalAddress?.street2 || "",
    zip: payPalAddress?.zip || ""
  };

  const creditCardLocation = {
    city: city.value,
    state: state.value
      ? state.value
      : states && states[0]
      ? states[0].code
      : "",
    street1: street1.value,
    street2: street2.value,
    zip: zip.value
  };

  const address =
    payPalToken && payPalAddress ? payPalLocation : creditCardLocation;

  const [taxAddress, setTaxAddress] = useState<TaxAddress>({ ...address });

  useEffect(() => {
    if (
      !zip.error &&
      address &&
      city.value &&
      state.value &&
      street2.value &&
      zip
    ) {
      setTaxAddress({ ...address });
    }
  }, [address, city.value, state.value, street2.value, zip, payPalAddress]);

  const [rivalsEmailsAgreed, setRivalsEmailsAgreed] = useState(false);
  const [thirdPartyEmailsAgreed, setThirdPartyEmailsAgreed] = useState(false);

  const dirty = useRef(false);

  const [formValues, setFormValues] = useState({
    email: "",
    errors: {
      email: "",
      password: "",
      username: ""
    },
    password: "",
    username: ""
  });

  const [selectedPlan, selectPlan] = useState<PurchasablePlan>(
    (plan as PurchasablePlan) || PLANS.ANNUAL
  );

  const selectedAddOn = useSelector<AppState, AddOn | undefined>(state =>
    selectSelectedAddOn(state, selectedPlan)
  );

  const [promoCode, setPromoCode] = useState(promotionCode as string);
  const [ncidReferralCode] = useState(ncid as string);

  const subscribableAddOns = useSelector<AppState, AddOn[]>(state =>
    selectSubscribableAddOns(state, selectedPlan)
  );

  useError({
    formValues,
    registrationError,
    setFormValues
  });

  const debounceFn = debounce((name: string, value: string) => {
    if (name === "email") {
      validateEmailOrUsername({ property: "email", value });
    } else {
      validateEmailOrUsername({ property: "username", value });
    }
  }, EMAIL_USERNAME_CHANGE_SLEEPTIME);

  const inuptCallback = useCallback(debounceFn, [
    debounceFn,
    validateEmailOrUsername
  ]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dirty.current = true;
    const { name, value } = e.currentTarget;

    setFormValues({
      ...formValues,
      [name]: value
    });

    if (value && (name === "username" || name === "email")) {
      inuptCallback(name, value);
    }
  };

  const handleInputBlur = (e: ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const { name, value } = e.target;

    switch (name) {
      case "email":
        setFormValues({
          ...formValues,
          errors: {
            ...formValues.errors,
            email: isValidEmail(value) ? "" : emailError
          }
        });
        if (payPalToken) {
          resetPayPal();
          updatePayPalError(payPalAccountError);
        }
        setPayPalEnabledEmail(value);
        validateEmailOrUsername({ property: "email", value: value });
        break;
      case "password":
        setFormValues({
          ...formValues,
          errors: {
            ...formValues.errors,
            password: isValidPassword(value) ? "" : passwordError
          }
        });
        break;
      case "username":
        validateEmailOrUsername({ property: "username", value: value });
        if (
          formValues.errors.username &&
          registrationParams &&
          registrationParams.username !== formValues.username
        ) {
          clearAlerts();
          setFormValues({
            ...formValues,
            errors: { ...formValues.errors, username: "" }
          });
        }
        break;
      default:
        break;
    }
  };

  const handleSelectedSite = (value: Site): void => {
    setSelectedSite(value);
  };

  const handleSelectedSiteCleared = (): void => {
    setSelectedSite(currentSite);
  };

  const handleRivalsEmailAgreement = (): void => {
    setRivalsEmailsAgreed(!rivalsEmailsAgreed);
  };

  const handleThirdPartyEmailAgreement = (): void => {
    setThirdPartyEmailsAgreed(!thirdPartyEmailsAgreed);
  };

  const tosAgreements = {
    handleRivalsEmailAgreement,
    handleThirdPartyEmailAgreement,
    rivalsEmailsAgreed,
    thirdPartyEmailsAgreed
  };

  const siteSelector = (
    <SiteSelectionStep
      handleSelectedSite={handleSelectedSite}
      onClear={handleSelectedSiteCleared}
      stepNumber={STEP_NUMBERS.TWO}
    />
  );

  const getPayPalError = (): string => {
    if (payPalError) {
      return payPalError;
    }
    if (!isEmailValid) {
      return payPalEmailError;
    }
    return "";
  };

  const emailAlreadyTaken = validationError && validationError.email;
  const usernameAlreadyTaken = validationError && validationError.username;
  const validationErrorPresent =
    (emailAlreadyTaken || usernameAlreadyTaken) !== undefined;
  const isEmailValid =
    !!formValues.email && !formValues.errors.email && !emailAlreadyTaken;
  const isPayPalEnabled =
    isEmailValid && payPalEnabledEmail === formValues.email;

  const formData = {
    debitCardAgreement: isDebitAgreed,
    email: formValues.email,
    password: formValues.password,
    recaptchaToken: "payment-verification",
    rivalsEmails: rivalsEmailsAgreed,
    siteSubscription: {
      autoRenewalOptIn: billingFormProps.isAutoRenewAgreed,
      gaClientId: selectedSite?.googleAnalyticsMeasurementId,
      ncidReferralCode: ncidReferralCode,
      promoCode: promoCode,
      siteId: selectedSite ? selectedSite.id : undefined,
      subscriptionPlan: selectedPlan
    },
    thirdPartyEmails: thirdPartyEmailsAgreed,
    username: formValues.username,
    usingDebitCard: billingFormProps.isDebit
  };

  useEffect(() => {
    dirty.current = true;
  }, [selectedSite, selectedPlan]);

  useEffect(() => {
    const handleFormAbandon = (): void => {
      if (dirty.current && !submitClicked && !user) {
        sendGAEvent(CUSTOM_EVENTS.abandonForm("subscription_form"));
      }
    };
    window.addEventListener("beforeunload", handleFormAbandon);
    router.events.on("routeChangeStart", handleFormAbandon);

    return (): void => {
      window.removeEventListener("beforeunload", handleFormAbandon);
      router.events.off("routeChangeStart", handleFormAbandon);
    };
  }, [router.events, sendGAEvent, submitClicked, user]);

  const addressContainerRef = useRef<HTMLDivElement>(null);

  const [
    showAddressVerificationModal,
    setShowAddressVerificationModal
  ] = useState(false);

  const completePurchase = (): void => {
    setSubmitClicked(true);
    if (selectedSite) {
      sendGAEvent(
        CUSTOM_EVENTS.createSubscription(
          "subscription",
          selectedPlan,
          selectedSite.name
        )
      );

      const submitObj: RegistrationParams = {
        ...formData,
        paypalEmail: payPalEmail,
        siteSubscription: {
          ...formData.siteSubscription,
          gaClientId: selectedSite?.googleAnalyticsMeasurementId,
          siteId: selectedSite.id
        }
      };

      // Currently only able to select either ad lite or all access
      if (selectedAddOn) {
        submitObj.addOnName = selectedAddOn.name;
      }
      submit(submitObj);
    }
  };

  const onPurchase = (): void => {
    // no address validation when selected payment method is paypal
    if (payPalToken) {
      completePurchase();
      return;
    }

    if (!pathname.includes("test")) {
      completePurchase();
      return;
    }

    // verify estimate tax didn't return an invalid address error
    if (taxEstimationError?.toLowerCase() !== INVALID_ADDRESS) {
      completePurchase();
    } else {
      setShowAddressVerificationModal(true);
    }
  };

  if (!pricingPropsPresent || !billingPropsPresent) {
    return null;
  }

  return (
    <div>
      <CreateAccountStep
        emailFeedback={formValues.errors.email || emailAlreadyTaken}
        formValues={formValues}
        handleInputBlur={handleInputBlur}
        handleInputChange={handleInputChange}
        passwordFeedback={formValues.errors.password}
        stepNumber={STEP_NUMBERS.ONE}
        userBirthdateComponent={null}
        usernameFeedback={formValues.errors.username || usernameAlreadyTaken}
      />
      {!isSiteSubscribable && siteSelector}
      <ConfirmSubscriptionStep
        annualCostDollars={annualCostDollars}
        currentSiteName={selectedSite ? selectedSite.name : ROOT_SITE_NAME}
        monthlyCostDollars={monthlyCostDollars}
        monthlySavings={monthlySavings}
        promoCode={promoCode}
        referAFriendCode={referAFriendCode as string}
        selectPlan={selectPlan}
        selectedPlan={selectedPlan}
        selectedSite={selectedSite}
        setPromoCode={setPromoCode}
        stepNumber={isSiteSubscribable ? STEP_NUMBERS.TWO : STEP_NUMBERS.THREE}
        subscribableAddOns={subscribableAddOns}
      />
      <BillingInformationStep
        {...billingFormProps}
        addressContainerRef={addressContainerRef}
        isPayPalEnabled={isPayPalEnabled}
        payPalError={getPayPalError()}
        stepNumber={isSiteSubscribable ? STEP_NUMBERS.THREE : STEP_NUMBERS.FOUR}
        userEmail={formValues.email}
      />
      {selectedSite && (
        <CompletePurchaseStep
          address={taxAddress}
          buttonDisabled={
            submitting ||
            isCheckingOut ||
            !valid ||
            isRedirecting ||
            invalidUserInput(formValues) ||
            validationErrorPresent ||
            !!getPayPalError() ||
            selectedSite.friendlyName.toLowerCase() ===
              ROOT_SITE_IDENTIFIER.toLowerCase()
          }
          debitAgreedState={debitAgreedState}
          debitTerms={debitTerms}
          isAutoRenewAgreed={isAutoRenewAgreed}
          isDebit={billingFormProps.isDebit}
          payPalEmail={payPalEmail}
          promoCodeValue={promoCode}
          requiresAutoRenewalOptIn={requiresAutoRenewalOptIn}
          selectedAddOn={selectedAddOn}
          selectedPlan={selectedPlan}
          selectedSite={selectedSite}
          setIsAutoRenewAgreed={setIsAutoRenewAgreed}
          stepNumber={
            isSiteSubscribable ? STEP_NUMBERS.FOUR : STEP_NUMBERS.FIVE
          }
          submit={onPurchase}
          tax={tax}
          tosAgreements={tosAgreements}
        />
      )}
      {showAddressVerificationModal && (
        <AddressVerificationModal
          addressContainerRef={addressContainerRef}
          changeVisibility={setShowAddressVerificationModal}
          formAddress={{
            city: billingFormProps.billingFormState.city.value,
            state: billingFormProps.billingFormState.state.value,
            street1: billingFormProps.billingFormState.street1.value,
            street2: billingFormProps.billingFormState.street2.value,
            zip: billingFormProps.billingFormState.zip.value
          }}
          siteId={selectedSite?.id}
          smartyAuthToken={billingConstants.smartyStreetsWebToken}
          updateAddress={billingFormProps.updateAddress}
          visible={showAddressVerificationModal}
        />
      )}
    </div>
  );
};

export default CreateAccount;
